import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { NewsArticle, ROUTE_PATHS, formatDate } from '@/lib/index';
import { springPresets } from '@/lib/motion';

interface NewsCardProps {
  article: NewsArticle;
}

export function NewsCard({ article }: NewsCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      whileHover={{ y: -4 }}
      className="h-full"
    >
      <Link to={ROUTE_PATHS.NEWS_DETAIL.replace(':id', article.id)}>
        <Card className="h-full overflow-hidden group hover:shadow-lg transition-all duration-300">
          <div className="relative overflow-hidden aspect-video">
            <motion.img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.05 }}
              transition={springPresets.gentle}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
              <Tag className="w-3 h-3 mr-1" />
              {article.category}
            </Badge>
          </div>

          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <time dateTime={article.date}>{formatDate(article.date)}</time>
            </div>
            <h3 className="text-xl font-semibold line-clamp-2 group-hover:text-primary transition-colors">
              {article.title}
            </h3>
          </CardHeader>

          <CardContent>
            <p className="text-muted-foreground line-clamp-3">
              {article.excerpt}
            </p>
          </CardContent>

          <CardFooter>
            <motion.div
              className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all"
              whileHover={{ x: 4 }}
            >
              Read More
              <ArrowRight className="w-4 h-4" />
            </motion.div>
          </CardFooter>
        </Card>
      </Link>
    </motion.div>
  );
}
